import { useSelector } from 'react-redux'
import {
  selectCustomerIsRealEstateAgent,
  selectHomebotNetworkEnabled
} from '../../../../store/selectors/customerProfile'
import {
  selectPreferredRealEstateAgentMembership,
  selectPreferredLoanOfficerMembership
} from '../../../../store/selectors/clientTeamMemberships'
import { RealEstateAgentCard } from '../RealEstateAgentCard/RealEstateAgentCard'
import { UnverifiedAgentCard } from './UnverifiedAgentCard'
import { VerifiedAgentCard } from './VerifiedAgentCard'
import { NoPreferredAgentCard } from './NoPreferredAgentCard'
import { VerificationFlowProvider, VerificationFlowManager } from '../../../shared/VerificationFlow'
import { Listing } from '../../../../api/gqlaxy/gql/generated/graphql'
import { selectClientFetchId, selectClientFirstHome } from '../../../../store/selectors/client'
import { useVerificationFlowEnabled } from '../../../../hooks/useVerificationFlowEnabled'

interface ListingMessageCardContainerProps {
  listing: Listing
}

export const ListingMessageCardContainer = ({ listing }: ListingMessageCardContainerProps) => {
  const isVerificationFlowEnabled = useVerificationFlowEnabled()
  const isRealEstateAgent = useSelector(selectCustomerIsRealEstateAgent)
  const clientId = useSelector(selectClientFetchId)
  const clientFirstHome = useSelector(selectClientFirstHome)

  const preferredRealEstateAgentMembership = useSelector(selectPreferredRealEstateAgentMembership)
  const preferredLoanOfficerMembership = useSelector(selectPreferredLoanOfficerMembership)
  const streetAddress = clientFirstHome?.streetAddress || listing?.address?.street || ''

  // 🎯 HACK: Wrap existing components in VerificationFlowProvider when enabled
  const renderCard = () => {
    // Legacy flow - use existing components without verification
    if (isRealEstateAgent || !isVerificationFlowEnabled) {
      return <RealEstateAgentCard listing={listing} />
    }

    if (preferredRealEstateAgentMembership?.isVerified) {
      return <VerifiedAgentCard listing={listing} clientTeamMembership={preferredRealEstateAgentMembership} />
    }

    if (preferredRealEstateAgentMembership && !preferredRealEstateAgentMembership.isVerified) {
      return (
        <UnverifiedAgentCard
          listing={listing}
          clientTeamMembership={preferredRealEstateAgentMembership}
          clientId={clientId}
          streetAddress={streetAddress}
        />
      )
    }

    return <NoPreferredAgentCard listing={listing} />
  }

  const cardComponent = renderCard()

  // Only wrap with verification flow if enabled AND not in legacy flow
  if (isVerificationFlowEnabled && !isRealEstateAgent) {
    return (
      <VerificationFlowProvider
        options={{
          enableVerificationFlow: true,
          initialPartnerType: 'LoanOfficer',
          realEstateAgentMembership: preferredRealEstateAgentMembership,
          loanOfficerMembership: preferredLoanOfficerMembership,
          prequalOnly: true // 🎯 Skip messaging step and go directly to prequal
        }}
      >
        {cardComponent}
        <VerificationFlowManager />
      </VerificationFlowProvider>
    )
  }

  return cardComponent
}
